/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiType } from '@squiz/dxp-ai-types';
import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  ButtonProps,
  DROPDOWN_POSITION_RIGHT,
  DialogProps,
  Dropdown,
  DropdownMenuProps,
  ICON_DELETE_FOREVER,
  ICON_MORE_HORIZ,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import * as ConsoleState from 'dxpConsoleCore-mf/State';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { getAiService } from '../../Services/ai';
import { isGenerative } from '../../Utilities/formPredicates';
import { ViewAiMoreDropdownInput } from './ViewAiMoreDropdown.props';

export const ViewAiMoreDropdown = ({ aiId, type }: ViewAiMoreDropdownInput): JSX.Element => {
  const [deleteProps, setDeleteProps] = useState<DialogProps | null>(null);
  const navigate = useNavigate();
  const toolbarButtonProps: ButtonProps = {
    format: BUTTON_FORMAT_SOFT,
    theme: BUTTON_THEME_DEFAULT,
  };
  const { updateActiveDialog } = useDialogStore();
  const aiService = getAiService();
  const { t } = useTranslation();

  const getDeleteDialogProps = (updateActiveDialog: Function, deleteContentTool: Function): DialogProps => {
    const closeDeleteDialog = (): void => updateActiveDialog(false);
    const DialogContent = (
      <div>
        <p>{t(isGenerative(type) ? AI_STRINGS.deleteContentTool : AI_STRINGS.deleteChatbot)}</p>
        <p>{t(AI_STRINGS.deleteDialogText2)}</p>
      </div>
    );
    return {
      buttons: [
        <Button
          format={BUTTON_FORMAT_SOFT}
          key="delete-ai"
          onClick={closeDeleteDialog}
          text="Cancel"
          theme={BUTTON_THEME_DEFAULT}
        />,
        <Button key="cancel-delete-ai" onClick={() => deleteContentTool()} text="Delete" theme={BUTTON_THEME_DANGER} />,
      ],
      dialogContent: { renderContent: () => <div>{DialogContent}</div> },
      heading: t(isGenerative(type) ? AI_STRINGS.deleteContentToolHeading : AI_STRINGS.deleteChatbotHeading),
      icon: ICON_DELETE_FOREVER,
      onClose: () => closeDeleteDialog(),
      stateHandler: ConsoleState.useConsoleServiceStore,
    };
  };

  const deleteContentTool = async (): Promise<void> => {
    const props = getDeleteDialogProps(updateActiveDialog, deleteContentTool);
    setDeleteProps({ ...props, loading: true });
    try {
      if (!aiId) {
        setDeleteProps({ ...props, errorMessage: 'No content tool id' });
        return;
      }
      let path = '/';
      if (isGenerative(type)) {
        await aiService.apiClient.generative.delete({ id: aiId });
        path = getCapabilityPath(`/ai-content-tools`);
      } else {
        await aiService.apiClient.chatbot.delete({ id: aiId });
        path = getCapabilityPath(`/ai-chatbots`);
      }
      navigate(path);
    } catch (e) {
      if (e instanceof Error) {
        setDeleteProps({ ...props, errorMessage: e.message, loading: false });
      }
    }
  };

  useEffect(() => {
    updateActiveDialog(deleteProps);
  }, [deleteProps, updateActiveDialog]);

  let canDeleteTool = hasPermission(PERMISSIONS.dxpAi.GENERATIVE_WRITE) && type === AiType.generative;

  canDeleteTool = canDeleteTool || (hasPermission(PERMISSIONS.dxpAi.CHATBOT_WRITE) && type === AiType.chatbot);

  const deleteItems = [];
  if (canDeleteTool) {
    deleteItems.push({
      action: (): void => {
        setDeleteProps(getDeleteDialogProps(updateActiveDialog, deleteContentTool));
      },
      key: 'delete-ai',
      label: 'Delete',
    });
  }

  const dropdownMenu: DropdownMenuProps = {
    buttonProps: {
      ...toolbarButtonProps,
      icon: ICON_MORE_HORIZ,
      label: `Toggle more actions menu`,
    },
    dropdownPosition: DROPDOWN_POSITION_RIGHT,
    sections: [
      {
        items: deleteItems,
        key: 'ai-moreActions-delete',
      },
    ],
  };
  return <div>{deleteItems.length ? <Dropdown {...dropdownMenu} /> : <></>}</div>;
};
